/**
 * Audience — restrict a Service, an Event or a Queue to students whose SIS
 * attributes match (JAI-2020). The ONE place the three Studio editors render,
 * read and summarise it.
 *
 * Data: partial (2026-10-01, kit 1de470d7 + completion + deps).
 *   - READ bound: `listAudienceAttributes({ subjectKind, locale })` →
 *     GET /v1/admin/catalog/audience-attributes — observed 200 in the preview
 *     (changelog/2026-10-01T0300-audience-ui-binding.md). Loaded once per
 *     subject + locale and shared by every editor on the page.
 *   - WRITE rides each editor's existing save (`audience` on the service /
 *     event / queue body): sent only when it changed, `null` = everyone.
 *     Service save observed; event + queue saves are wired but go to the
 *     serving engine until the kit's event/queue families mount.
 *   - Value labels come ONLY from `options[].label`. `values` is never shown;
 *     a null program label reads "Unknown program", never the id.
 * Types are the contract's (`@v0y4ge/api-contracts` src/audience.ts).
 */
import { useEffect, useState, type CSSProperties } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { isApiError, listAudienceAttributes } from "@v0y4ge/api-client";
import type {
  Audience as ContractAudience,
  AudienceCondition as ContractCondition,
  AudienceAttribute as ContractAttribute,
  AudienceSubjectKind,
  AudienceComparisonOperator,
} from "@v0y4ge/api-contracts";
import { NumberField } from "./NumberField";
import { currentLocale, type t as tFn } from "./i18n";
import { getConfirmedTenantSlug, onTenantChange, onTenantSwitching } from "../shell/access";

type Translate = typeof tFn;

export type AudienceSubject = AudienceSubjectKind;
export type AudienceCompareOp = AudienceComparisonOperator;
export type AudienceCondition = ContractCondition;
export type Audience = ContractAudience;
export type AudienceAttribute = ContractAttribute;
export const AUDIENCE_MAX_CONDITIONS = 20;

export type AudienceAttributesRead =
  | { status: "loading" }
  | { status: "ready"; attributes: AudienceAttribute[] }
  | { status: "failed" };

/* Same base the service catalog choke-points use (services-data.ts REQUEST_OPTS). */
const REQUEST_OPTS = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
};
const cache = new Map<string, Promise<AudienceAttribute[]>>();
/* LOCAL PATCH (platform side, 2026-10-02, drop-2026-1002c): attributes are one tenant's SIS vocabulary. The
   cache is keyed by the confirmed tenant and emptied the instant a switch starts, so
   tenant B never reads tenant A's keys or labels. */
onTenantSwitching(() => cache.clear());

/** The attributes this tenant's SIS supplies for `subject`, in the UI locale.
 *  `ready` with an EMPTY list means the tenant has no attribute source — every
 *  audience save would be refused, so the section disables itself. */
export function useAudienceAttributes(subject: AudienceSubject): AudienceAttributesRead {
  const locale = currentLocale();
  const [, setTenantGen] = useState(0);
  useEffect(() => onTenantChange(() => setTenantGen((n) => n + 1)), []);
  const key = (getConfirmedTenantSlug() ?? "") + "|" + subject + "|" + locale;
  const [read, setRead] = useState<AudienceAttributesRead>({ status: "loading" });
  useEffect(() => {
    let live = true;
    let p = cache.get(key);
    if (!p) {
      p = listAudienceAttributes({ subjectKind: subject, locale }, REQUEST_OPTS).then((r) => r.data as AudienceAttribute[]);
      cache.set(key, p);
      p.catch(() => cache.delete(key));
    }
    setRead({ status: "loading" });
    p.then((attributes) => { if (live) setRead({ status: "ready", attributes }); },
      () => { if (live) setRead({ status: "failed" }); });
    return () => { live = false; };
  }, [key, subject, locale]);
  return read;
}

/** Omit-vs-send: true when the edited value differs from the stored one. */
export function audienceChanged(stored: unknown, edited: unknown): boolean {
  return JSON.stringify(readAudience(stored)) !== JSON.stringify(readAudience(edited));
}

/** 503 AUDIENCE_STORE_UNAVAILABLE → whether the rest of the offering saved. */
export function audienceStoreUnavailable(err: unknown): { offeringSaved: boolean } | null {
  if (!isApiError(err) || err.code !== "AUDIENCE_STORE_UNAVAILABLE") return null;
  const params = (err as { params?: { offeringSaved?: unknown } }).params;
  return { offeringSaved: params?.offeringSaved === true };
}

/** 403 AUDIENCE_FORBIDDEN on a book / register / join door → the approved
 *  refusal sentence, or null. Never the server's message or the raw reason. */
export function audienceRefusalCopy(t: (key: string, fallback: string) => string, err: unknown): string | null {
  const e = (err || {}) as { code?: unknown; params?: unknown; body?: { error?: { code?: unknown; params?: unknown } } };
  const code = e.code ?? e.body?.error?.code;
  if (code !== "AUDIENCE_FORBIDDEN") return null;
  const params = (e.params ?? e.body?.error?.params ?? {}) as { reason?: unknown; subjectKind?: unknown };
  if (params.reason === "sign_in_required") return t("audience.denied.signIn", "Sign in to see if this is available to you.");
  const kind = params.subjectKind === "event" || params.subjectKind === "queue" ? params.subjectKind : "service";
  return t(`audience.denied.notEligible.${kind}`, DENIED_EN[kind]);
}
const DENIED_EN: Record<AudienceSubject, string> = {
  service: "This service isn’t available for your student profile.",
  event: "This event isn’t available for your student profile.",
  queue: "This queue isn’t available for your student profile.",
};

/* ── reading a stored value ─────────────────────────────────────────────── */

const COMPARE_OPS: readonly AudienceCompareOp[] = ["gt", "gte", "lt", "lte"];

/** A stored value as a clean `Audience`, or null for "Everyone". Anything that
 *  is not an object with conditions is null. A malformed condition is kept visible as far
 *  as it can be read rather than dropped. */
export function readAudience(value: unknown): Audience | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = (value as { conditions?: unknown }).conditions;
  if (!Array.isArray(raw)) return null;
  const conditions: AudienceCondition[] = [];
  for (const c of raw) {
    if (!c || typeof c !== "object") continue;
    const r = c as Record<string, unknown>;
    const attribute = typeof r.attribute === "string" ? r.attribute : "";
    if (r.kind === "attribute-compare") {
      const op = COMPARE_OPS.includes(r.operator as AudienceCompareOp) ? (r.operator as AudienceCompareOp) : "gt";
      conditions.push({ kind: "attribute-compare", attribute, operator: op, value: typeof r.value === "number" ? r.value : 0 });
    } else if (r.kind === "attribute-in" || r.kind === "attribute-not-in") {
      const values = Array.isArray(r.values) ? r.values.map(String) : [];
      conditions.push({ kind: r.kind, attribute, values });
    }
  }
  return conditions.length ? { conditions } : null;
}

/* ── labels ─────────────────────────────────────────────────────────────── */

type OpId = "in" | "notIn" | AudienceCompareOp;
const OP_EN: Record<OpId, string> = {
  in: "is one of", notIn: "is not one of",
  gt: "greater than", gte: "at least", lt: "less than", lte: "at most",
};
function opOf(c: AudienceCondition): OpId {
  return c.kind === "attribute-in" ? "in" : c.kind === "attribute-not-in" ? "notIn" : c.operator;
}
export function audienceOperatorLabel(t: Translate, op: OpId): string {
  return t(`audience.operator.${op}`, OP_EN[op]);
}

const humanise = (k: string) => k.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const camelKey = (k: string) => k.replace(/[_-]+([a-z0-9])/gi, (_, c: string) => c.toUpperCase());
/** Attribute NAMES reuse the JAI-1894 customer-detail keys, so "Class year"
 *  reads the same on the student record and on the rule that selects them. */
const ATTR_EN: Record<string, string> = { program: "Program", level: "Level", classification: "Class year", gpa: "GPA" };
export function audienceAttributeLabel(t: Translate, key: string): string {
  if (key === "program") return t("customers.detail.sis.program", "Program");
  if (key === "gpa") return t("audience.attribute.gpa", "GPA");
  return t(`customers.detail.sis.attr.${camelKey(key)}`, ATTR_EN[key] ?? humanise(key));
}

type ValueLabel = (attribute: string, value: string) => string | null;
/** Label lookup over the read's `options`. */
function labelsFrom(attrs: AudienceAttribute[] | null): ValueLabel {
  const map = new Map<string, string | null>();
  for (const a of attrs ?? []) for (const o of a.options ?? []) map.set(`${a.key}\u0000${o.value}`, o.label);
  return (attribute, value) => map.get(`${attribute}\u0000${value}`) ?? null;
}

/** A value as a person reads it. A program id is NEVER printed. */
function valueText(t: Translate, attribute: string, value: string, label: ValueLabel): string {
  const served = label(attribute, value);
  if (served) return served;
  if (attribute === "program") return t("audience.value.unknownProgram", "Unknown program");
  return humanise(value);
}

/** One condition as a sentence: "Program is one of Computer Science, Economics". */
export function audienceConditionSentence(t: Translate, c: AudienceCondition, label: ValueLabel): string {
  const op = audienceOperatorLabel(t, opOf(c));
  const rhs = c.kind === "attribute-compare"
    ? String(c.value)
    : c.values.map((v) => valueText(t, c.attribute, v, label)).join(", ");
  return `${audienceAttributeLabel(t, c.attribute)} ${op} ${rhs}`;
}

/* ── 422 AUDIENCE_INVALID → per-condition messages ──────────────────────── */

type Part = "attribute" | "operator" | "values" | "value" | "condition";
const PART_EN: Record<Part, string> = {
  attribute: "Choose an attribute your SIS supplies.",
  operator: "This comparison isn't allowed for this attribute.",
  values: "One or more values aren't offered by your SIS.",
  value: "Enter a number.",
  condition: "Check this condition.",
};
export type AudienceErrors = Record<number, string[]>;

/** One message per problem, keyed by condition index. The server's own words
 *  are never shown — they are developer English; the path picks catalog copy. */
export function audienceErrorsFrom(t: Translate, err: unknown): AudienceErrors | null {
  if (!isApiError(err) || err.code !== "AUDIENCE_INVALID") return null;
  const out: AudienceErrors = {};
  const fields = (err as { fields?: unknown }).fields;
  for (const f of Array.isArray(fields) ? fields : []) {
    const rawPath = (f as { path?: unknown }).path;
    const path = Array.isArray(rawPath) ? rawPath.join(".") : String(rawPath ?? "");
    const m = /conditions[.[](\d+)\]?(?:\.(\w+))?/.exec(path);
    const idx = m ? Number(m[1]) : 0;
    const part = (m && m[2] && m[2] in PART_EN ? m[2] : "condition") as Part;
    const msg = t(`audience.error.${part}`, PART_EN[part]);
    const list = (out[idx] ||= []);
    if (!list.includes(msg)) list.push(msg);
  }
  if (!Object.keys(out).length) out[0] = [t("audience.error.condition", PART_EN.condition)];
  return out;
}

/* ── read views ─────────────────────────────────────────────────────────── */

/** List chip — "Audience: 2 conditions". Nothing at all when there is none. */
export function AudienceChip({ value, t, style }: { value: unknown; t: Translate; style?: CSSProperties }) {
  const a = readAudience(value);
  if (!a) return null;
  const n = a.conditions.length;
  return (
    <span data-testid="audience-summary-chip" style={{
      display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 999,
      border: `1px solid ${T.n4}`, background: T.n6, color: T.n2,
      fontFamily: T.font, fontSize: 11.5, fontWeight: 600, ...style,
    }}>{t("audience.summary", "{count, plural, one {Audience: # condition} other {Audience: # conditions}}", { count: n })}</span>
  );
}

/* ── the editor section ─────────────────────────────────────────────────── */

const INPUT: CSSProperties = {
  minHeight: 34, padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.n4}`,
  background: T.white, color: T.n1, fontFamily: T.font, fontSize: 13, minWidth: 0, maxWidth: "100%",
};

function blankFor(attr: AudienceAttribute): AudienceCondition {
  return attr.kind === "ordinal"
    ? { kind: "attribute-compare", attribute: attr.key, operator: "gte", value: attr.range?.min ?? 0 }
    : { kind: "attribute-in", attribute: attr.key, values: [] };
}

export function AudienceSection({ value, onChange, t, subject, disabled, errors, sectionError, attributes, style }: {
  value: unknown;
  /** `null` = Everyone. Called only from local edits; the editor's own save writes it. */
  onChange: (next: Audience | null) => void;
  t: Translate;
  subject: AudienceSubject;
  disabled?: boolean;
  errors?: AudienceErrors | null;
  /** A section-level error (503 AUDIENCE_STORE_UNAVAILABLE), shown under the rows. */
  sectionError?: string | null;
  /** The attributes read when the caller already holds it (tests; a host that
   *  reads once for several sections). Omitted → useAudienceAttributes. */
  attributes?: AudienceAttributesRead;
  style?: CSSProperties;
}) {
  const hookRead = useAudienceAttributes(subject);
  const read = attributes ?? hookRead;
  const audience = readAudience(value);
  const conditions = audience?.conditions ?? [];
  const attrs = read.status === "ready" ? read.attributes : null;
  const label = labelsFrom(attrs);
  const attrByKey = new Map((attrs ?? []).map((a) => [a.key, a] as const));
  const noSource = !!attrs && attrs.length === 0;
  const editable = !disabled && !!attrs && !noSource;
  const [adding, setAdding] = useState(false); // ui-local

  const write = (next: AudienceCondition[]) => onChange(next.length ? { conditions: next } : null);
  const patch = (i: number, c: AudienceCondition) => write(conditions.map((x, k) => (k === i ? c : x)));
  const remove = (i: number) => write(conditions.filter((_, k) => k !== i));
  const add = (key: string) => {
    const a = attrByKey.get(key);
    if (a) write([...conditions, blankFor(a)]);
    setAdding(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, ...style }} data-testid="audience-section" data-subject={subject}>
      {conditions.length === 0 ? (
        <div style={{ fontSize: 13, color: T.n2, fontFamily: T.font }} data-testid="audience-everyone">
          {t("audience.everyone", "Everyone (no audience)")}
        </div>
      ) : conditions.map((c, i) => {
        const attr = attrByKey.get(c.attribute) ?? null;
        const unavailable = !!attrs && !noSource && !attr;
        const errs = errors?.[i] ?? [];
        return (
          <div key={i} data-testid="audience-condition" data-id={String(i)} style={{
            display: "flex", flexDirection: "column", gap: 8, padding: 10, borderRadius: 8,
            border: `1px solid ${unavailable || errs.length ? T.orange1 : T.n5}`, background: T.white,
          }}>
            {editable && attr ? (
              <ConditionEditor t={t} c={c} attr={attr} attrs={attrs!} label={label}
                onChange={(next) => patch(i, next)} onRemove={() => remove(i)} />
            ) : (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                <span style={{ flex: "1 1 180px", minWidth: 0, fontSize: 13, color: T.n1, fontFamily: T.font, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                  {audienceConditionSentence(t, c, label)}
                </span>
                {editable ? <RemoveBtn t={t} onClick={() => remove(i)} /> : null}
              </div>
            )}
            {unavailable ? (
              <div role="status" data-testid="audience-attribute-unavailable" style={{ fontSize: 11.5, color: T.orange1, lineHeight: 1.5, fontFamily: T.font }}>
                {t("audience.attributeUnavailable", "This attribute is no longer supplied by your SIS — nobody matches this condition.")}
              </div>
            ) : null}
            {errs.map((m) => (
              <div key={m} role="alert" data-testid="audience-condition-error" style={{ fontSize: 11.5, color: T.red1, lineHeight: 1.5, fontFamily: T.font }}>{m}</div>
            ))}
          </div>
        );
      })}

      {sectionError ? (
        <div role="alert" data-testid="audience-section-error" style={{ fontSize: 12, color: T.red1, lineHeight: 1.5, fontFamily: T.font }}>{sectionError}</div>
      ) : null}
      {disabled ? null : (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          {adding && attrs ? (
            <select autoFocus aria-label={t("audience.addCondition", "Add condition")} defaultValue=""
              onChange={(e) => e.target.value && add(e.target.value)} onBlur={() => setAdding(false)} style={INPUT}>
              <option value="" disabled>{t("audience.chooseAttribute", "Choose an attribute")}</option>
              {attrs.map((a) => <option key={a.key} value={a.key}>{audienceAttributeLabel(t, a.key)}</option>)}
            </select>
          ) : (
            /* ui-local: opens the attribute picker; the editor's save writes the value */
            <button type="button" data-testid="audience-add-condition"
              disabled={!editable || conditions.length >= AUDIENCE_MAX_CONDITIONS}
              onClick={() => setAdding(true)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, minHeight: 34, padding: "6px 12px",
                borderRadius: 999, border: `1px dashed ${T.n4}`, background: T.white, color: T.p1,
                fontFamily: T.font, fontSize: 12.5, fontWeight: 600,
                cursor: editable ? "pointer" : "default", opacity: editable ? 1 : 0.55,
              }}>+ {t("audience.addCondition", "Add condition")}</button>
          )}
          {noSource ? (
            <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5, fontFamily: T.font }}>
              {t("audience.pendingAttributes", "Available once your SIS attributes are connected.")}
            </span>
          ) : read.status === "failed" ? (
            <span role="status" style={{ fontSize: 11.5, color: T.red1, lineHeight: 1.5, fontFamily: T.font }}>
              {t("audience.attributesFailed", "Couldn't load your SIS attributes.")}
            </span>
          ) : null}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5, fontFamily: T.font }}>
        {t(`audience.help.${subject}`, HELP_EN[subject])}
      </div>
    </div>
  );
}

const HELP_EN: Record<AudienceSubject, string> = {
  service: "Only students who match every condition see and can book this service. Signed-out visitors don't see it.",
  event: "Only students who match every condition see and can register for this event. Signed-out visitors don't see it.",
  queue: "Only students who match every condition see and can join this queue. Signed-out visitors don't see it.",
};

function RemoveBtn({ t, onClick }: { t: Translate; onClick: () => void }) {
  return (
    /* ui-local: removes a condition from the form; the editor's save writes it */
    <button type="button" onClick={onClick} data-testid="audience-remove-condition"
      aria-label={t("audience.removeCondition", "Remove condition")}
      style={{ flex: "none", width: 34, height: 34, borderRadius: 6, border: `1px solid ${T.n4}`, background: T.white, color: T.n2, cursor: "pointer", fontSize: 13 }}>✕</button>
  );
}

function ConditionEditor({ t, c, attr, attrs, label, onChange, onRemove }: {
  t: Translate; c: AudienceCondition; attr: AudienceAttribute; attrs: AudienceAttribute[];
  label: ValueLabel;
  onChange: (c: AudienceCondition) => void; onRemove: () => void;
}) {
  const ops: OpId[] = attr.kind === "ordinal" ? [...COMPARE_OPS] : ["in", "notIn"];
  const op = opOf(c);
  const setOp = (next: OpId) => {
    if (next === "in" || next === "notIn") {
      const values = c.kind === "attribute-compare" ? [] : c.values;
      onChange({ kind: next === "in" ? "attribute-in" : "attribute-not-in", attribute: c.attribute, values });
    } else {
      onChange({ kind: "attribute-compare", attribute: c.attribute, operator: next, value: c.kind === "attribute-compare" ? c.value : 0 });
    }
  };
  const setAttr = (key: string) => {
    const a = attrs.find((x) => x.key === key);
    if (a) onChange(blankFor(a));
  };
  const selected = new Set(c.kind === "attribute-compare" ? [] : c.values);
  const offered = attr.options?.map((o) => o.value) ?? attr.values ?? [];
  const pickable = [...offered, ...[...selected].filter((v) => !offered.includes(v))];
  const toggle = (v: string) => {
    if (c.kind === "attribute-compare") return;
    const order = pickable;
    const vals = order.filter((x) => (x === v ? !selected.has(x) : selected.has(x)));
    onChange({ ...c, values: vals });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <select value={c.attribute} onChange={(e) => setAttr(e.target.value)} aria-label={t("audience.attribute", "Attribute")} style={{ ...INPUT, flex: "1 1 140px" }}>
          {attrs.map((a) => <option key={a.key} value={a.key}>{audienceAttributeLabel(t, a.key)}</option>)}
        </select>
        <select value={op} onChange={(e) => setOp(e.target.value as OpId)} aria-label={t("audience.operator", "Comparison")} style={{ ...INPUT, flex: "1 1 130px" }}>
          {ops.map((o) => <option key={o} value={o}>{audienceOperatorLabel(t, o)}</option>)}
        </select>
        <RemoveBtn t={t} onClick={onRemove} />
      </div>
      {c.kind === "attribute-compare" ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <NumberField decimal value={c.value} min={-1e9} onChange={(n) => onChange({ ...c, value: n })}
            placeholder={attr.range ? `${attr.range.min}–${attr.range.max}` : undefined}
            ariaLabel={audienceAttributeLabel(t, attr.key)} style={{ ...INPUT, width: 110 }} />
          {attr.range ? (
            <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.font }}>
              {t("audience.rangeHint", "Scale {min}–{max}", { min: attr.range.min, max: attr.range.max })}
            </span>
          ) : null}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {/* Offered values first, then any stored value the read no longer offers —
            a saved selection is never hidden just because the picker is empty. */}
        {pickable.length ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {pickable.map((v) => {
            const on = selected.has(v);
            return (
              /* ui-local: selection only — the value is written by the editor's own save */
              <button key={v} type="button" role="checkbox" aria-checked={on} onClick={() => toggle(v)}
                data-testid="audience-value-option" data-id={v}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7, minHeight: 34, padding: "6px 12px",
                  borderRadius: 999, fontFamily: T.font, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${on ? T.p1 : T.n4}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n2,
                  maxWidth: "100%", overflowWrap: "anywhere", textAlign: "left",
                }}>
                <span aria-hidden style={{
                  width: 14, height: 14, borderRadius: 4, flex: "none", border: `1px solid ${on ? T.p1 : T.n4}`,
                  background: on ? T.p1 : T.white, display: "inline-flex", alignItems: "center", justifyContent: "center",
                  color: T.white, fontSize: 10, lineHeight: 1,
                }}>{on ? "✓" : ""}</span>
                {valueText(t, attr.key, v, label)}
              </button>
            );
          })}
        </div>
        ) : null}
        {attr.options && attr.options.length === 0 ? (
          <div data-testid="audience-no-options" style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5, fontFamily: T.font }}>
            {t("audience.noOptions", "Your SIS doesn't list any values for this attribute yet.")}
          </div>
        ) : null}
        </div>
      )}
    </div>
  );
}
